/**
 * AccountSettings.jsx (iter148) — Logged-in user's account page.
 * Currently hosts the Change Password card. Future: profile fields, MFA,
 * social-login linking, etc.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key, Eye, EyeSlash, ShieldCheck, Warning } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const validate = (pw) => {
  if (!pw || pw.length < 8) return 'Must be at least 8 characters.';
  if (!/[A-Za-z]/.test(pw)) return 'Must include a letter.';
  if (!/\d/.test(pw)) return 'Must include a digit.';
  return null;
};

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate(pw);
    if (v) { setError(v); return; }
    if (pw !== pw2) { setError('New passwords don\u2019t match.'); return; }
    if (pw === cur) { setError('New password must be different from your current one.'); return; }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: cur, new_password: pw },
        { withCredentials: true },
      );
      toast.success('Password updated. Please sign in with your new password.');
      if (r.data.session_invalidated) {
        // Bumping users.password_changed_at means the cookie we hold no longer
        // passes the iat-check in get_current_user — log out and bounce to login.
        await (logout ? logout() : Promise.resolve());
        setTimeout(() => navigate('/login'), 1000);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-3xl" data-testid="account-settings-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck size={28} weight="fill" className="text-brand" /> Account
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Signed in as <b>{user?.email}</b> · {user?.role}
          </p>
        </div>

        <div className="bg-card border border-border" data-testid="change-password-card">
          <div className="overline px-4 py-3 border-b border-border flex items-center gap-2">
            <Key size={14} weight="fill" /> Change password
          </div>
          <form onSubmit={submit} className="p-4 space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Updating your password will sign you out everywhere — you&apos;ll need to sign in again with the new password on every device.
            </p>

            {error && (
              <div data-testid="change-pw-error" className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md flex items-start gap-2">
                <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <Label htmlFor="cur" className="overline">Current password</Label>
              <Input
                id="cur"
                type={show ? 'text' : 'password'}
                value={cur}
                onChange={(e) => setCur(e.target.value)}
                placeholder="••••••••"
                data-testid="change-pw-current"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="pw" className="overline">New password</Label>
              <div className="relative mt-1">
                <Input
                  id="pw"
                  type={show ? 'text' : 'password'}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="••••••••"
                  data-testid="change-pw-new"
                  required
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="change-pw-show">
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Minimum 8 characters, with at least one letter and one digit.
              </p>
            </div>
            <div>
              <Label htmlFor="pw2" className="overline">Confirm new password</Label>
              <Input
                id="pw2"
                type={show ? 'text' : 'password'}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="••••••••"
                data-testid="change-pw-confirm"
                className="mt-1"
                required
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={loading || !cur || !pw} className="rounded-none bg-brand hover:bg-brand/90 text-white" data-testid="change-pw-submit">
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
